import { useState } from "react";
import { useSearchParams } from "next/navigation";

import createQueryParam from "@/helpers/createQueryParam";
import { useAppRouter } from "@/hooks/common/useAppRouter";
import { useTranslations } from "@/hooks/common/useTranslations";

import type {
  IFilter,
  IFilterAndSortingProps,
  ISorting,
  ITransformedSortOrFilter,
} from "./types";

const useFilterAndSorting = ({
  filterConstants,
  sortConstants,
  defaultValueFilter,
  defaultValueSorting,
}: IFilterAndSortingProps) => {
  const { t } = useTranslations("common");
  const router = useAppRouter();

  const searchParams = useSearchParams();

  const transformedFiltersValue: ITransformedSortOrFilter[] =
    filterConstants.map(({ name, value }) => ({
      name: t(name),
      value,
    }));

  const transformedSortValues: ITransformedSortOrFilter[] = sortConstants.map(
    ({ name, value }) => ({
      name: t(name),
      value,
    }),
  );

  const getQueryParamValue = (key: string) => searchParams.get(key);

  const getSelectedItem = (
    defaultValue: string,
    constants: IFilter[] | ISorting[],
    param?: string,
  ) => {
    return (
      constants.find(({ value, column }) => {
        const paramValue = getQueryParamValue(param || column);

        return value === paramValue;
      }) || constants.find(({ value }) => value === defaultValue)
    );
  };

  const initialCurrentFilter = getSelectedItem(
    defaultValueFilter,
    filterConstants,
  );

  const initialCurrentSorting = getSelectedItem(
    defaultValueSorting,
    sortConstants,
    "sortBy",
  );

  const [currentFilter, setCurrentFilter] = useState<IFilter>(
    initialCurrentFilter as IFilter,
  );

  const [currentSort, setCurrentSort] = useState(
    initialCurrentSorting as ISorting,
  );

  const onChange = (
    filterOrSortingValue: string,
    constantsSortOrFilter: IFilter[] | ISorting[],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    setCurrent: Function,
    defaultValueSortOrFilter: string,
    queryParamName?: string,
    // eslint-disable-next-line max-params
  ) => {
    const currentItem =
      constantsSortOrFilter.find(
        ({ value }) => value === filterOrSortingValue,
      ) ||
      constantsSortOrFilter.find(
        ({ value }) => value === defaultValueSortOrFilter,
      );

    if (!currentItem) {
      return;
    }

    setCurrent(currentItem);

    const actionQueryParam =
      currentItem.value === defaultValueSortOrFilter ? "delete" : "create";

    const queryParams = createQueryParam(
      { [queryParamName || currentItem.column]: [currentItem.value] },
      actionQueryParam,
    );

    // eslint-disable-next-line consistent-return
    return router.appReplace(queryParams, { scroll: false });
  };

  const onChangeFilter = (filterValue: string) => {
    return onChange(
      filterValue,
      filterConstants,
      setCurrentFilter,
      defaultValueFilter,
    );
  };

  const onChangeSort = (sortValue: string) => {
    return onChange(
      sortValue,
      sortConstants,
      setCurrentSort,
      defaultValueSorting,
      "sortBy",
    );
  };

  return {
    currentFilter,
    transformedFiltersValue,
    onChangeFilter,
    currentSort,
    transformedSortValues,
    onChangeSort,
  };
};

export default useFilterAndSorting;
